import { useContext } from 'react';
import { useAuthenticated, useDataProvider } from 'react-admin';

import { AppContext } from '@app/contexts';
import { useGenericList } from '@app/hooks/useGenericList';
import { CASLAction, useAbility } from '@app/utils/authUtils';
import { getFieldOnMainLanguage } from '@app/utils/languages';
import { prependHttpsIfProtocolNotPresent } from '@app/utils/urlUtils';

import { GenericList } from '@app/components/common/GenericList';
import { ColumnTypes, DeleteConfirmation, GenericObject } from '@app/components/common/Table/DefaultTable/types';

import { ExpertsFilter } from './ExpertsFilter';

export const ListExperts = () => {
  const { handleChangeLoading } = useContext(AppContext);
  const ability = useAbility();
  const dataProvider = useDataProvider();

  const {
    isLoading,
    data,
    total,
    handleChangeSort,
    handleChangeFilters,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  } = useGenericList('experts', {
    sort: { field: 'date_created', order: 'DESC' },
    filter: { expertiseIds: [], companyId: null, name: '' },
  });

  /**
   * List[X] pages require manual auth check for some reason.
   * This involves the `checkAuth` method in auth.ts.
   */
  useAuthenticated();

  const resolveDeleteConfirmationContent = async (companyId: number): Promise<DeleteConfirmation> => {
    const response = await dataProvider.fetchLinks('experts', companyId);
    const links = response?.data;
    return {
      deletable: true,
      message:
        links.Subscribers > 0
          ? `This expert is currently being followed by ${links.Subscribers} on the website. Are you sure you want to delete this expert?`
          : 'Are you sure you want to delete this expert?',
    } as DeleteConfirmation;
  };

  const columns = [
    {
      label: 'NAME',
      key: 'name',
      getImagePath: (record: GenericObject) => `${(record?.expert_photo_file_base as GenericObject)?.path}`,
      type: ColumnTypes.TITLE,
      format: (row: GenericObject) =>
        `${getFieldOnMainLanguage(row?.first_name)} ${getFieldOnMainLanguage(row?.last_name)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'first_name', order });
      },
    },
    {
      label: 'ROLE',
      key: 'role',
      type: ColumnTypes.DEFAULT,
      format: (row: GenericObject) => `${getFieldOnMainLanguage(row?.expert_roles)}`,
      sort: (order: string) => {
        handleChangeSort({ field: 'expert_roles', order });
      },
    },
    {
      label: 'COMPANY',
      key: 'company',
      type: ColumnTypes.LINK_SAME_BROWSER_TAB,
      format: (row: GenericObject) =>
        row?.company ? `${getFieldOnMainLanguage((row?.company as GenericObject)?.name)}` : '',
      getLink: (row: GenericObject) => (row?.company ? `/companies/${(row?.company as GenericObject)?.id}` : ''),
      sort: (order: string) => {
        handleChangeSort({ field: 'Company.name', order });
      },
    },
    {
      label: 'EXPERTISE',
      key: 'expertise_names',
      type: ColumnTypes.PILLS,
      format: (row: GenericObject) => getFieldOnMainLanguage(row?.category_name),
    },
    {
      label: 'TWITTER',
      key: 'twitterurl',
      type: ColumnTypes.LINK_NEW_BROWSER_TAB,
      format: (row: GenericObject) => `${row?.twitterurl ?? ''}`,
      getLink: (row: GenericObject) =>
        row?.twitterurl ? prependHttpsIfProtocolNotPresent(row?.twitterurl as string) : '',
    },
  ];

  if (ability.can(CASLAction.WRITE, 'experts')) {
    columns.push({
      label: 'PUBLISHED',
      key: 'enabled',
      type: ColumnTypes.STATUS,
      // @ts-ignore this is definitely valid
      publishedKey: 'enabled',
      isPublished: (row: GenericObject) => !!row.enabled,
      getNewPublishedStatus: (row: GenericObject) => !row.enabled,
    });
  }

  return (
    <GenericList
      isListLoading={isLoading}
      SearchFields={ExpertsFilter}
      rows={data}
      generalFilterKey={'name'}
      resourceSingular="expert"
      handleChangeLoading={handleChangeLoading}
      columns={columns}
      resource="experts"
      title="Experts"
      page={page}
      rowsPerPage={rowsPerPage}
      handleChangePage={handleChangePage}
      total={total}
      handleChangeRowsPerPage={handleChangeRowsPerPage}
      handleChangeFilters={handleChangeFilters}
      resolveDeleteConfirmationContent={resolveDeleteConfirmationContent}
    />
  );
};
